import React, { useState, useMemo } from 'react';
import {
  Objective,
  RatingScale,
  ObjectiveRating,
  TrackBy,
  TRACK_BY_LABELS,
} from '../../types/pms';
import ProgressSlider from './ProgressSlider';
import RatingDisplay from './RatingDisplay';
import './SelfReviewForm.css';

export interface SelfReviewSubmission {
  objectiveRatings: ObjectiveRating[];
  employeeRating: RatingScale;
  employeeRatingDescription: string;
  employeeComment: string;
}

interface SelfReviewFormProps {
  objectives: Objective[];
  onSubmit: (submission: SelfReviewSubmission) => void;
  onCancel?: () => void;
  submitting?: boolean;
  /**
   * The employee's previously submitted answers, when they are revising after a manager
   * return. Seeds the form so a revision is an edit of what they wrote, not a blank retype —
   * the manager asked them to change something specific, not to start again.
   */
  previousSubmission?: {
    objectiveRatings?: ObjectiveRating[];
    employeeRating?: RatingScale | '';
    employeeRatingDescription?: string;
    employeeComment?: string;
  };
  /** True when this is a resubmission after a return; changes the button label only. */
  isResubmission?: boolean;
}

/**
 * Employee's own self-assessment, filled in once HR opens the review phase.
 * Deliberately separate from ReviewForm (manager-only) — see the PMS plan doc:
 * the employee rates first and locks, only then does the manager see it and rate.
 */
const SelfReviewForm: React.FC<SelfReviewFormProps> = ({
  objectives,
  onSubmit,
  onCancel,
  submitting = false,
  previousSubmission,
  isResubmission = false,
}) => {
  const previousByObjective = useMemo(() => {
    const map = new Map<string, ObjectiveRating>();
    (previousSubmission?.objectiveRatings || []).forEach((r) => map.set(r.objectiveId, r));
    return map;
  }, [previousSubmission]);

  const [objectiveProgress, setObjectiveProgress] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    objectives.forEach((o) => {
      const prior = previousByObjective.get(o.id);
      // employeeProgressPercentage, never finalProgressPercentage: the latter is the manager's
      // figure and must not be presented back to the employee as their own answer.
      init[o.id] = prior?.employeeProgressPercentage ?? o.progressPercentage;
    });
    return init;
  });
  const [objectiveRating, setObjectiveRating] = useState<Record<string, RatingScale | ''>>(() => {
    const init: Record<string, RatingScale | ''> = {};
    objectives.forEach((o) => {
      const prior = previousByObjective.get(o.id);
      if (prior?.rating) init[o.id] = prior.rating;
    });
    return init;
  });
  const [objectiveComments, setObjectiveComments] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    objectives.forEach((o) => {
      const prior = previousByObjective.get(o.id);
      if (prior?.comments) init[o.id] = prior.comments;
    });
    return init;
  });

  const [employeeRating, setEmployeeRating] = useState<RatingScale | ''>(previousSubmission?.employeeRating || '');
  const [employeeRatingDescription, setEmployeeRatingDescription] = useState(previousSubmission?.employeeRatingDescription || '');
  const [employeeComment, setEmployeeComment] = useState(previousSubmission?.employeeComment || '');

  const [errors, setErrors] = useState<string[]>([]);

  const validate = (): string[] => {
    const errs: string[] = [];
    for (const obj of objectives) {
      if (obj.trackBy === TrackBy.RATING && !objectiveRating[obj.id]) {
        errs.push(`Self-rating required for "${obj.title}"`);
      }
      /*
       * The per-objective comment is required, like every other field on this form.
       *
       * It is the only place the employee explains the number beside it, and the manager
       * evaluates each objective against exactly that — a slider at 60% with no comment gives
       * them nothing to review. It was the one optional field here, so it was routinely left
       * blank and the objective reached the manager as a bare percentage.
       *
       * Named per objective, matching the self-rating message above: with several objectives on
       * one scrolling panel, "Comment is required" alone does not say which one to scroll to.
       */
      if (!(objectiveComments[obj.id] ?? '').trim()) {
        errs.push(`Comment required for "${obj.title}"`);
      }
    }
    if (!employeeRating) errs.push('Overall self-rating is required');
    if (!employeeRatingDescription.trim()) errs.push('Rating description is required');
    if (!employeeComment.trim()) errs.push('Comment is required');
    return errs;
  };

  const handleSubmit = () => {
    const validationErrors = validate();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors([]);

    const ratings: ObjectiveRating[] = objectives.map((obj) => {
      const isRatingTracked = obj.trackBy === TrackBy.RATING;
      return {
        objectiveId: obj.id,
        objectiveTitle: obj.title,
        /*
         * Only a rating the employee actually chose, and only where one was asked for.
         *
         * This used to read `objectiveRating[obj.id] || employeeRating || MEETS_EXPECTATIONS`.
         * Percent-tracked objectives render no rating control, so the first term was always
         * undefined and every one of them silently inherited the employee's *overall* rating —
         * a per-objective judgement the employee never made. Left undefined instead; the
         * weighted-rating calculator skips ratings that are absent.
         */
        ...(isRatingTracked && objectiveRating[obj.id]
          ? { rating: objectiveRating[obj.id] as RatingScale }
          : {}),
        /*
         * The self-assessment goes in its own field. finalProgressPercentage belongs to the
         * manager and is deliberately not written here — writing it was what let the manager's
         * later submission overwrite the employee's figure.
         */
        employeeProgressPercentage: objectiveProgress[obj.id] ?? obj.progressPercentage,
        finalProgressPercentage: 0,
        comments: (objectiveComments[obj.id] ?? '').trim(),
      };
    });

    onSubmit({
      objectiveRatings: ratings,
      employeeRating: employeeRating as RatingScale,
      employeeRatingDescription: employeeRatingDescription.trim(),
      employeeComment: employeeComment.trim(),
    });
  };

  return (
    <div className="pms-self-review-form">
      {/*
        No <h2>Self Review</h2> here. RightSidePanel already renders the drawer title and the
        close button in its own header, so this block was a second identical heading sitting
        below the first, with the explanation stranded under it — which is what made the top of
        the drawer read as misaligned. This is now just the explanation, directly beneath the
        panel's heading, left-aligned.
      */}
      <div className="pms-self-review-form__intro">
        <p className="pms-self-review-form__lede">
          Review your progress against each objective and add a brief comment explaining it.
        </p>
        <div className="pms-self-review-form__note">
          <strong>Why this matters</strong>
          <span>
            Your self-review is what your manager reads before completing their evaluation.
            You will not be able to edit it after submitting.
          </span>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="pms-form-error-summary" role="alert">
          <strong>Please fix the following:</strong>
          <ul>{errors.map((err, i) => <li key={i}>{err}</li>)}</ul>
        </div>
      )}

      {objectives.map((obj) => (
        <section key={obj.id} className="pms-self-review-form__objective">
          <div className="pms-self-review-form__obj-header">
            <h3 className="pms-self-review-form__obj-title">{obj.title}</h3>
            <span className="pms-self-review-form__obj-weightage">{obj.weightage}%</span>
          </div>

          {obj.description && (
            <p className="pms-self-review-form__obj-desc">{obj.description}</p>
          )}
          {obj.measureOfSuccess && (
            <p className="pms-self-review-form__obj-measure">
              <span className="pms-self-review-form__obj-measure-label">Measure of success</span>
              {obj.measureOfSuccess}
            </p>
          )}

          {obj.trackBy === TrackBy.RATING ? (
            <div className="pms-form-group">
              {/* The label names the tracking mode, because which control appears depends on it
                  and a rating field where progress was expected otherwise looks like a bug. */}
              <label className="pms-form-label">
                Self Rating <span className="required">*</span>
                <span className="pms-self-review-form__trackby-tag">
                  {TRACK_BY_LABELS[obj.trackBy || TrackBy.PERCENT]}
                </span>
              </label>
              <RatingDisplay
                rating={objectiveRating[obj.id]}
                interactive
                onChange={(value) => setObjectiveRating(prev => ({ ...prev, [obj.id]: value }))}
              />
            </div>
          ) : (
            <div className="pms-form-group pms-self-review-form__progress-group">
              <label className="pms-form-label">
                Self-Assessed Progress
                <span className="pms-self-review-form__trackby-tag">
                  {TRACK_BY_LABELS[obj.trackBy || TrackBy.PERCENT]}
                </span>
              </label>
              <ProgressSlider
                value={objectiveProgress[obj.id] ?? obj.progressPercentage}
                onChange={(value) => setObjectiveProgress(prev => ({ ...prev, [obj.id]: value }))}
                showMarks
                ariaLabel={`Self-assessed progress for ${obj.title}`}
              />
            </div>
          )}

          <div className="pms-form-group">
            <label className="pms-form-label" htmlFor={`self-comment-${obj.id}`}>
              Comment <span className="required">*</span>
            </label>
            <textarea
              id={`self-comment-${obj.id}`}
              className="pms-form-textarea"
              value={objectiveComments[obj.id] ?? ''}
              onChange={(e) => setObjectiveComments(prev => ({ ...prev, [obj.id]: e.target.value }))}
              placeholder="How did you do on this objective?"
              rows={3}
              disabled={submitting}
            />
          </div>
        </section>
      ))}

      {/* Final section, after every objective — never between them. */}
      <section className="pms-self-review-form__overall">
        <h3 className="pms-self-review-form__overall-title">Overall Self Assessment</h3>

        <div className="pms-form-group">
          <label className="pms-form-label">Rating <span className="required">*</span></label>
          {/* No `label` prop: the field already has its own "Rating *" label above, and passing
              one printed a second caption beside the stars. */}
          <RatingDisplay
            rating={employeeRating}
            interactive
            onChange={(value) => setEmployeeRating(value)}
          />
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="self-rating-description">
            Rating Description <span className="required">*</span>
          </label>
          <input
            id="self-rating-description"
            className="pms-form-input"
            type="text"
            value={employeeRatingDescription}
            onChange={(e) => setEmployeeRatingDescription(e.target.value)}
            placeholder="e.g. Exceeded expectations on AWS migration and reliability goals"
            disabled={submitting}
          />
        </div>

        <div className="pms-form-group">
          <label className="pms-form-label" htmlFor="self-comment">
            Comment <span className="required">*</span>
          </label>
          <textarea
            id="self-comment"
            className="pms-form-textarea"
            value={employeeComment}
            onChange={(e) => setEmployeeComment(e.target.value)}
            placeholder="Summarize your overall performance this period"
            rows={4}
            disabled={submitting}
          />
        </div>
      </section>

      {/* Sticky to the bottom of the panel's scroll area, so Submit is reachable without
          scrolling to the end of a long list of objectives. */}
      <div className="pms-form-actions pms-self-review-form__actions">
        {onCancel && (
          <button type="button" className="pms-btn pms-btn-outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        )}
        <button type="button" className="pms-btn pms-btn-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Submitting…' : isResubmission ? 'Resubmit Self Review' : 'Submit Self Review'}
        </button>
      </div>
    </div>
  );
};

export default SelfReviewForm;
